import React from 'react';
import PropTypes from 'prop-types';
import {
	IconBulb,
	IconTemperature,
	IconSun,
	IconUser,
	IconDroplet,
	IconGauge,
	IconBolt,
	IconHeadphones,
	IconWifi,
	IconDoor,
	IconQuestionMark,
} from '@tabler/icons-react';
import getType from 'utils/deviceType';

const TYPE_CONFIG = {
	light: { Icon: IconBulb, toggleable: true },
	temperature: { Icon: IconTemperature, toggleable: false },
	lightLevel: { Icon: IconSun, toggleable: false },
	presence: { Icon: IconUser, toggleable: false },
	humidity: { Icon: IconDroplet, toggleable: false },
	pressure: { Icon: IconGauge, toggleable: false },
	consumption: { Icon: IconBolt, toggleable: false },
	power: { Icon: IconBolt, toggleable: false },
	speaker: { Icon: IconHeadphones, toggleable: true },
	mobile: { Icon: IconUser, toggleable: false },
	network: { Icon: IconWifi, toggleable: false },
	door: { Icon: IconDoor, toggleable: false },
	unknown: { Icon: IconQuestionMark, toggleable: false },
};

function Toggle({ on, onClick }) {
	const handleKeyDown = (e) => {
		if (e.keyCode === 13 || e.keyCode === 32) onClick();
	};

	return (
		<div
			role="switch"
			aria-checked={on}
			tabIndex="0"
			onClick={onClick}
			onKeyDown={handleKeyDown}
			className="relative shrink-0 cursor-pointer transition-colors"
			style={{
				width: '28px',
				height: '16px',
				borderRadius: '8px',
				background: on ? '#76BC21' : 'var(--color-bg-tertiary)',
				border: `0.5px solid ${on ? '#76BC21' : 'var(--color-border-light)'}`,
			}}
		>
			<span
				className="absolute rounded-full bg-white transition-transform"
				style={{
					width: '12px',
					height: '12px',
					top: '1.5px',
					left: '1.5px',
					transform: on ? 'translateX(12px)' : 'translateX(0)',
				}}
			/>
		</div>
	);
}

Toggle.propTypes = {
	on: PropTypes.bool.isRequired,
	onClick: PropTypes.func.isRequired,
};

function DashboardCardItem({ item, onClick }) {
	if (!item || !item.values) return null;

	const { module, name, values } = item;
	let { description, guiOn } = values;

	if (module === 'sonos') {
		const { currentTrack, state } = values;
		if (state !== 'playing') {
			description = 'Stoppad';
		} else if (currentTrack !== undefined) {
			const { artist, title, uri } = currentTrack;
			description = artist !== null ? `${artist} – ${title}` : title;
			if (uri.endsWith('spdif')) description = 'TV';
		}
		guiOn = state === 'playing';
	}

	const type = getType(item);
	const { Icon, toggleable } = TYPE_CONFIG[type] || TYPE_CONFIG.unknown;

	const handleKeyDown = (e) => {
		if (e.keyCode === 13) onClick();
	};

	return (
		<div
			className="flex items-center gap-3 px-4 py-2.5 hover:bg-[--color-bg-secondary] transition-colors"
			onClick={toggleable ? undefined : onClick}
			onKeyDown={toggleable ? undefined : handleKeyDown}
			role={toggleable ? undefined : 'button'}
			tabIndex={toggleable ? undefined : 0}
		>
			<Icon
				size={14}
				className="shrink-0"
				style={{ color: guiOn ? '#76BC21' : 'var(--color-text-tertiary)' }}
			/>
			<span
				className="flex-1 text-[13px]"
				style={{ color: 'var(--color-text-primary)' }}
			>
				{name}
			</span>
			{toggleable ? (
				<Toggle on={!!guiOn} onClick={onClick} />
			) : (
				<span
					className="text-[12px] shrink-0"
					style={{ color: 'var(--color-text-secondary)' }}
				>
					{description}
				</span>
			)}
		</div>
	);
}

DashboardCardItem.propTypes = {
	item: PropTypes.shape({
		name: PropTypes.string,
		deviceId: PropTypes.string,
		module: PropTypes.string,
		data: PropTypes.shape({ type: PropTypes.string }),
		values: PropTypes.shape({
			description: PropTypes.string,
			guiOn: PropTypes.bool,
			currentTrack: PropTypes.shape({
				artist: PropTypes.string,
				title: PropTypes.string,
				uri: PropTypes.string,
			}),
			state: PropTypes.string,
		}).isRequired,
	}).isRequired,
	onClick: PropTypes.func.isRequired,
};

export default DashboardCardItem;
